import React, { useState, useMemo, useEffect } from "react";
import { Spin } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { notifyUser } from "../../../util/util";
import { extractErrorMessage } from '../../../hooks/extractErrorMessage';
import { authApi } from "../../../services/auth.service";
import { ReactComponent as LockIcon } from '../../../assets/UiElements/lock.svg';
import secureLocalStorage from "react-secure-storage";
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../lib/react-query';

/**
 * Post-Login Phone OTP Verification Page
 * 
 * This page is shown after a successful login when phone_verified is "false".
 * Uses otp_ref from login response to verify OTP (no phone input needed).
 * After successful verification, API returns token which we store.
 */
export default function PostLoginPhoneOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Get data passed from login
  const [otpRef, setOtpRef] = useState(location.state?.otpRef || "");
  const maskedPhone = location.state?.maskedPhone || "";
  
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(165); // 2:45 minutes
  const [shake, setShake] = useState(false);

  // Redirect if no otpRef (direct access without login)
  useEffect(() => {
    if (!otpRef) {
      notifyUser('error', 'Please login first', 'Error');
      navigate('/onboard/login');
    }
  }, [otpRef, navigate]);

  // Update otpRef if location state changes
  useEffect(() => {
    if (location.state?.otpRef) {
      setOtpRef(location.state.otpRef);
    }
  }, [location.state?.otpRef]);

  // Timer countdown
  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setInterval(() => setSeconds((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [seconds]);

  const formatTime = useMemo(() => (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const secs = timeInSeconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  }, []);

  const remainingTime = useMemo(() => 
    seconds > 45 ? formatTime(seconds - 45) : null, 
    [seconds, formatTime]
  );

  // Verify OTP using otp_ref
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      notifyUser('error', 'Please enter the 6-digit OTP received to proceed!', 'Validation Error');
      return;
    }

    if (!otpRef) {
      notifyUser('error', 'Session expired. Please login again.', 'Error');
      navigate('/onboard/login');
      return;
    }

    setLoading(true);
    try {
      // Verify OTP with otp_ref and otp
      const response = await authApi.verifyOtp({ otp_ref: otpRef, otp });
      
      if (response.data?.success) {
        // Check if data contains token (expected format)
        const responseData = response.data.data;
        let token = null;
        let userData = {};
        
        // Handle different response formats
        if (Array.isArray(responseData) && responseData.length === 0) {
          // API returned empty array - phone verified but no token
          // Need to login again
          notifyUser("success", response.data.message || "Phone verified successfully! Please login to continue.", "Success");
          navigate("/onboard/login");
          return;
        } else if (responseData && typeof responseData === 'object') {
          token = responseData.token;
          userData = { ...responseData };
          delete userData.token;
        }
        
        if (!token) {
          // No token returned - redirect to login
          notifyUser("success", response.data.message || "Phone verified successfully! Please login to continue.", "Success");
          navigate("/onboard/login");
          return;
        }
        
        // Token received - complete login
        secureLocalStorage.setItem('token', token);
        secureLocalStorage.setItem('user', JSON.stringify(userData));
        secureLocalStorage.setItem('affluent', userData.affluent);
        
        // Cache the profile in React Query
        queryClient.setQueryData(queryKeys.auth.user, { success: true, data: userData });
        queryClient.refetchQueries({ queryKey: queryKeys.auth.user, exact: true });
        
        notifyUser("success", "Phone verified! Welcome to your dashboard.", "Success");
        navigate("/dashboard");
      } else {
        throw new Error(response.data?.message || 'OTP verification failed');
      }
    } catch (e) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      notifyUser('error', extractErrorMessage(e, 'OTP verification failed'), 'Error');
    }
    setLoading(false);
  };

  // Resend OTP using otp_ref
  const resendOTP = async () => {
    if (seconds > 45) return;
    
    setShake(true);
    setSeconds(165);
    setTimeout(() => setShake(false), 500);
    
    try {
      // Resend using otp_ref
      const response = await authApi.resendOtp(otpRef);
      // Update otp_ref from response if available (new otp_ref for subsequent calls)
      const newOtpRef = response.data?.data?.otp_ref;
      if (newOtpRef) {
        setOtpRef(newOtpRef);
      }
      notifyUser("success", response.data?.message || "OTP has been resent successfully!", "Success");
    } catch (e) {
      notifyUser('error', extractErrorMessage(e, 'Failed to resend OTP'), 'Error');
    }
  };

  const handleOtpChange = (e) => setOtp(e.target.value);

  return (
    <div className='mx-auto mobile-full-width shadow bg-white p-5 rounded login-width'>
      <div align='center'>
        <p className='lead fw-bold'>Verify Your Phone Number</p>
        <small className="text-center text-muted">
          For your security, please verify your phone number to continue.
        </small>
        
        {/* Show masked phone from login */}
        {maskedPhone && (
          <div className="mt-3 mb-2">
            <small className="text-center">
              We've sent a 6-digit OTP to <strong>{maskedPhone}</strong>
            </small>
          </div>
        )}
        
        <Spin spinning={loading}>
          <div className="mt-4 w-75 mx-auto mobile-full-width">
            
            {/* OTP Input */}
            <omk-otp-field
              number-of-inputs="6"
              resend-text=""
              resend-button-label=""
              label="One-time Password"
              className={`mb-1 ${shake ? "shake" : ""}`}
              value={otp}
              onInput={handleOtpChange}
            />
            
            <div className="d-flex justify-content-center align-items-center mb-3">
              <p className="text-center omk-text-small text-black-100 mb-1">
                Didn't receive the OTP?&nbsp;
              </p>
              <div className="text-center">
                {remainingTime ? (
                  <p className="text-center omk-text-small text-black-50 mb-1">
                    Resend in {remainingTime}
                  </p>
                ) : (
                  <p
                    className="text-center omk-text-small mb-1"
                    style={{ 
                      color: "#28a745", 
                      fontWeight: "bold", 
                      textDecoration: "underline", 
                      cursor: "pointer" 
                    }}
                    onClick={resendOTP}
                  >
                    Resend OTP
                  </p>
                )}
              </div>
            </div>
            
            <div className="d-flex justify-content-center">
              <p className="text-center omk-text-small text-black-50 mb-5" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LockIcon style={{ marginRight: "5px", width: "16px", height: "16px", verticalAlign: "middle" }} />
                Your information is confidential and secure
              </p>
            </div>
            
            <button
              onClick={handleVerifyOtp}
              className={`btn ${otp.length !== 6 ? "btn-secondary" : "btn-primary"} mb-4 w-50 mobile-full-width`}
              disabled={otp.length !== 6 || loading}
              style={{ 
                borderRadius: "50px", 
                padding: otp.length !== 6 ? "5px 20px" : "10px 20px", 
                height: "auto" 
              }}
            >
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>
            
            <div className="mt-3">
              <p
                className="text-center omk-text-small mb-1"
                style={{ 
                  color: "#666", 
                  textDecoration: "underline", 
                  cursor: "pointer" 
                }}
                onClick={() => navigate('/onboard/login')}
              >
                Back to Login
              </p>
            </div>
          </div>
        </Spin>
        
        <style>{`
          @keyframes shake {
            0% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            50% { transform: translateX(5px); }
            75% { transform: translateX(-5px); }
            100% { transform: translateX(0); }
          }
          .shake {
            animation: shake 0.5s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
}
